"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import InfoButton from "@/components/utils/button/InfoButton";
import ClosedButton from "@/components/utils/button/ClosedButton";
import ClockIcon from "@/components/utils/icons/ClockIcon";
import CalendarClosedIcon from "@/components/utils/icons/CalendarClosedIcon";
import LimitIcon from "@/components/utils/icons/LimitIcon";
import PermissionIcon from "@/components/utils/icons/PermissionIcon";
import { ApiWave } from "@/generated/models/ApiWave";
import { useWave, SubmissionStatus } from "@/hooks/useWave";
import { useCountdown } from "@/hooks/useCountdown";
interface MyStreamWaveTabsMemeSubmitProps {
  readonly wave: ApiWave;
  readonly handleMemesSubmit: () => void;
}

/**
 * Button component for The Memes submissions with multiple states:
 * - Coming soon (blue info button with countdown)
 * - Active (green primary button)
 *   - With submission count indicator when limited submissions remaining
 *   - With urgency indicator when submission period is ending soon
 * - Maximum submissions reached (blue info button, disabled)
 * - Not eligible (blue info button, disabled, with permission icon)
 * - Closed (gray disabled button)
 *
 * Uses useWave hook to determine submission status, eligibility,
 * time windows, and submission limits
 */
const MyStreamWaveTabsMemeSubmit: React.FC<MyStreamWaveTabsMemeSubmitProps> = t0 => {
  const $ = _c(61);
  const {
    wave,
    handleMemesSubmit
  } = t0;
  const waveInfo = useWave(wave);
  const targetTime = waveInfo?.participation.status === SubmissionStatus.NOT_STARTED ? waveInfo?.participation.startTime : null;
  const countdown = useCountdown(targetTime);
  const endTimeForCountdown = waveInfo?.participation.endTime ?? null;
  const endingCountdown = useCountdown(endTimeForCountdown);
  waveInfo?.participation.canSubmitNow;
  const canSubmit = waveInfo?.participation.canSubmitNow ?? false;
  if (!waveInfo) {
    return null;
  }
  const submissionStatus = waveInfo.participation.status;
  const isClosed = submissionStatus === SubmissionStatus.ENDED;
  const isComingSoon = submissionStatus === SubmissionStatus.NOT_STARTED;
  const openingTime = waveInfo.participation.startTime;
  const closingTime = waveInfo.participation.endTime;
  if (isClosed) {
    let t1;
    if ($[0] !== closingTime) {
      t1 = closingTime ? `Submissions closed on ${new Date(closingTime).toLocaleString()}` : "Submissions are closed";
      $[0] = closingTime;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const tooltipText = t1;
    let t2;
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CalendarClosedIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" />;
      t3 = <span>Submissions Closed</span>;
      $[2] = t2;
      $[3] = t3;
    } else {
      t2 = $[2];
      t3 = $[3];
    }
    let t4;
    if ($[4] !== tooltipText) {
      t4 = <ClosedButton title={tooltipText}>{t2}{t3}</ClosedButton>;
      $[4] = tooltipText;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (isComingSoon) {
    let t1;
    if ($[6] !== openingTime) {
      t1 = openingTime ? `Submissions open on ${new Date(openingTime).toLocaleString()}` : "Submissions will open soon";
      $[6] = openingTime;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const tooltipText_0 = t1;
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ClockIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== countdown) {
      t3 = <span>Submissions Open {countdown}</span>;
      $[9] = countdown;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3 || $[12] !== tooltipText_0) {
      t4 = <InfoButton disabled={true} title={tooltipText_0} variant="info">{t2}{t3}</InfoButton>;
      $[11] = t3;
      $[12] = tooltipText_0;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (waveInfo.participation.isEligible === false) {
    let t1;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <InfoButton disabled={true} title="You don't have permission to submit to this wave" variant="muted"><PermissionIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" /><span>Not Eligible to Submit</span></InfoButton>;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    return t1;
  }
  if (waveInfo.participation.hasReachedLimit) {
    const maxSubmissions = waveInfo.participation.maxSubmissions ?? "?";
    const submissionText = maxSubmissions === 1 ? "1 submission" : `${maxSubmissions} submissions`;
    const tooltipText_2 = `You have already submitted the maximum allowed (${submissionText})`;
    let t1;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LimitIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" />;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    const t2 = maxSubmissions === 1 ? "Submission Limit Reached (1)" : `Submission Limit Reached (${maxSubmissions})`;
    let t3;
    if ($[16] !== t2) {
      t3 = <span>{t2}</span>;
      $[16] = t2;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    let t4;
    if ($[18] !== t3 || $[19] !== tooltipText_2) {
      t4 = <InfoButton disabled={true} title={tooltipText_2} variant="muted">{t1}{t3}</InfoButton>;
      $[18] = t3;
      $[19] = tooltipText_2;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    return t4;
  }
  const hasLimitedRemaining = waveInfo.participation.remainingSubmissions !== null && waveInfo.participation.remainingSubmissions <= 3 && waveInfo.participation.remainingSubmissions > 0;
  const now = Date.now();
  const endTime = waveInfo.participation.endTime;
  const timeRemaining = endTime ? endTime - now : null;
  const isEndingVerySoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 3600000 && timeRemaining > 0;
  const isEndingHighlyUrgent = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 21600000 && timeRemaining > 0;
  const isEndingSoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 86400000 && timeRemaining > 0;
  let t1;
  if ($[21] !== canSubmit || $[22] !== endTime || $[23] !== hasLimitedRemaining || $[24] !== isEndingHighlyUrgent || $[25] !== isEndingSoon || $[26] !== isEndingVerySoon || $[27] !== waveInfo.participation.remainingSubmissions) {
    t1 = canSubmit ? hasLimitedRemaining ? `You have ${waveInfo.participation.remainingSubmissions} submission${waveInfo.participation.remainingSubmissions === 1 ? "" : "s"} remaining` : isEndingVerySoon ? `URGENT: Submissions closing in less than an hour! Ends at ${new Date(endTime).toLocaleString()}` : isEndingHighlyUrgent ? `Submissions closing in a few hours! Ends at ${new Date(endTime).toLocaleString()}` : isEndingSoon ? `Submissions closing soon! Ends on ${new Date(endTime).toLocaleString()}` : "Submit your art for The Memes" : "You cannot submit at this time";
    $[21] = canSubmit;
    $[22] = endTime;
    $[23] = hasLimitedRemaining;
    $[24] = isEndingHighlyUrgent;
    $[25] = isEndingSoon;
    $[26] = isEndingVerySoon;
    $[27] = waveInfo.participation.remainingSubmissions;
    $[28] = t1;
  } else {
    t1 = $[28];
  }
  const tooltipText_3 = t1;
  const t2 = !canSubmit;
  let t3;
  if ($[29] !== canSubmit || $[30] !== isEndingHighlyUrgent || $[31] !== isEndingSoon || $[32] !== isEndingVerySoon) {
    t3 = isEndingVerySoon && canSubmit ? <ClockIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-red-500 tw-animate-pulse" /> : isEndingHighlyUrgent && canSubmit ? <ClockIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-amber-500 tw-animate-pulse" /> : isEndingSoon && canSubmit ? <ClockIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-amber-400" /> : <svg className="tw-w-5 tw-h-5 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M7.5 12H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 7.5V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[29] = canSubmit;
    $[30] = isEndingHighlyUrgent;
    $[31] = isEndingSoon;
    $[32] = isEndingVerySoon;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  const t4 = !canSubmit ? "Submit Work to The Memes" : isEndingVerySoon ? "Submit Meme" : isEndingHighlyUrgent ? "Submit Meme" : isEndingSoon ? "Submit Meme (Closes Soon!)" : "Submit Work to The Memes";
  let t5;
  if ($[34] !== t4) {
    t5 = <span>{t4}</span>;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== canSubmit || $[37] !== endingCountdown || $[38] !== isEndingVerySoon) {
    t6 = isEndingVerySoon && canSubmit && <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-red-600 tw-border tw-border-white/30 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white tw-animate-pulse">Closing {endingCountdown}!</span>;
    $[36] = canSubmit;
    $[37] = endingCountdown;
    $[38] = isEndingVerySoon;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  let t7;
  if ($[40] !== canSubmit || $[41] !== endingCountdown || $[42] !== isEndingHighlyUrgent || $[43] !== isEndingVerySoon) {
    t7 = isEndingHighlyUrgent && !isEndingVerySoon && canSubmit && <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-600 tw-border tw-border-white/30 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white">Closing {endingCountdown}</span>;
    $[40] = canSubmit;
    $[41] = endingCountdown;
    $[42] = isEndingHighlyUrgent;
    $[43] = isEndingVerySoon;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  let t8;
  if ($[45] !== canSubmit || $[46] !== hasLimitedRemaining || $[47] !== isEndingVerySoon || $[48] !== waveInfo.participation.remainingSubmissions) {
    t8 = hasLimitedRemaining && !isEndingVerySoon && canSubmit && <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-primary-600 tw-border tw-border-white/30 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white">{waveInfo.participation.remainingSubmissions === 1 ? "Last One!" : `${waveInfo.participation.remainingSubmissions} Left`}</span>;
    $[45] = canSubmit;
    $[46] = hasLimitedRemaining;
    $[47] = isEndingVerySoon;
    $[48] = waveInfo.participation.remainingSubmissions;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] !== t5 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-gap-2">{t5}{t6}{t7}{t8}</div>;
    $[50] = t5;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== handleMemesSubmit || $[56] !== t2 || $[57] !== t3 || $[58] !== t9 || $[59] !== tooltipText_3) {
    t10 = <PrimaryButton loading={false} disabled={t2} onClicked={handleMemesSubmit} padding="tw-px-2.5 tw-py-2" title={tooltipText_3}>{t3}{t9}</PrimaryButton>;
    $[55] = handleMemesSubmit;
    $[56] = t2;
    $[57] = t3;
    $[58] = t9;
    $[59] = tooltipText_3;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  return t10;
};
export default MyStreamWaveTabsMemeSubmit;